import { useState, useCallback, useRef } from 'react';
import axios from 'axios';

const MAX_SEARCHES_PER_WINDOW = 3;
const RATE_LIMIT_WINDOW_MS = 3 * 60 * 1000; // 3 minutes
const MAX_STOPS_TO_SEARCH = 5;

/**
 * Custom hook for searching a bus on previous stops along the route
 * when it's not visible at the current stop.
 * Uses batch endpoint for efficient server-side search.
 *
 * @param {string} currentStopNumber - The current bus stop number
 * @returns {Object} { searchForBus, isSearching, searchResults }
 */
const useRouteSearch = (currentStopNumber) => {
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState({});
  const searchTimestamps = useRef([]);

  /**
   * Check if user has exceeded local rate limit
   */
  const isLocallyRateLimited = useCallback(() => {
    const now = Date.now();
    // Clean old timestamps
    searchTimestamps.current = searchTimestamps.current.filter(
      ts => now - ts < RATE_LIMIT_WINDOW_MS
    );
    return searchTimestamps.current.length >= MAX_SEARCHES_PER_WINDOW;
  }, []);

  /**
   * Search for a specific bus route on previous stops using batch endpoint
   */
  const searchForBus = useCallback(async (routeCode, direction = 'outbound') => {
    // Check local rate limit first
    if (isLocallyRateLimited()) {
      const result = { found: false, localRateLimited: true };
      setSearchResults(prev => ({ ...prev, [routeCode]: result }));
      return result;
    }

    // Record this search
    searchTimestamps.current.push(Date.now());

    setIsSearching(true);

    try {
      // Use batch endpoint - single request instead of multiple
      const response = await axios.post('/api/BusStops/search-route', {
        routeCode,
        currentStopNumber,
        direction,
        maxStops: MAX_STOPS_TO_SEARCH
      });

      const data = response.data;

      if (data.found) {
        const result = {
          found: true,
          notStarted: false,
          stopsAway: data.stopsAway,
          timeInMinutes: data.timeInMinutes,
          estimatedTime: data.additionalStops,
          foundAtStop: data.foundAtStopNumber,
          vehicleId: data.vehicleId,
          routeName: data.routeName,
          searchedStops: data.additionalStops
        };
        setSearchResults(prev => ({ ...prev, [routeCode]: result }));
        return result;
      }

      // Not found
      if (data.message === 'No previous stops available') {
        const result = { found: false, notStarted: true, stopsSearched: 0 };
        setSearchResults(prev => ({ ...prev, [routeCode]: result }));
        return result;
      }

      if (data.message === 'Search limit reached') {
        const result = {
          found: false,
          stopsLimited: true,
          stopsSearched: data.stopsSearched
        };
        setSearchResults(prev => ({ ...prev, [routeCode]: result }));
        return result;
      }

      // Route not found on previous stops
      const result = {
        found: false,
        notStarted: true,
        stopsSearched: data.stopsSearched || 0
      };
      setSearchResults(prev => ({ ...prev, [routeCode]: result }));
      return result;

    } catch (error) {
      console.error(`Error searching for bus ${routeCode}:`, error);

      // Handle rate limit error from server
      if (error.response?.status === 429) {
        const result = { found: false, rateLimited: true };
        setSearchResults(prev => ({ ...prev, [routeCode]: result }));
        return result;
      }

      // Handle not found errors
      if (error.response?.status === 404) {
        const errorData = error.response.data;
        if (errorData?.error === 'Current stop not found in route') {
          const result = { found: false, stopNotInRoute: true };
          setSearchResults(prev => ({ ...prev, [routeCode]: result }));
          return result;
        }
        if (errorData?.error === 'Route not found or has no stops') {
          const result = { found: false, noRouteData: true };
          setSearchResults(prev => ({ ...prev, [routeCode]: result }));
          return result;
        }
      }

      const result = { found: false, error: true };
      setSearchResults(prev => ({ ...prev, [routeCode]: result }));
      return result;
    } finally {
      setIsSearching(false);
    }
  }, [currentStopNumber, isLocallyRateLimited]);

  const clearSearchResult = useCallback((routeCode) => {
    setSearchResults(prev => {
      const newResults = { ...prev };
      delete newResults[routeCode];
      return newResults;
    });
  }, []);

  const clearAllResults = useCallback(() => {
    setSearchResults({});
  }, []);

  return {
    searchForBus,
    isSearching,
    searchResults,
    clearSearchResult,
    clearAllResults
  };
};

export default useRouteSearch;
